import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import FocusTrap from 'focus-trap-react';
import type { TutorialStep } from '@/lib/tutorials/engine';
import { zIndex } from '@/styles/variants';
import { buttonVariants } from '@markeroo/theme';
import { ui } from '@/strings';
import { Pill } from '@/components/Pill';
import { Spinner } from '@/components/Spinner';

const TOOLTIP_WIDTH = 480; // px (kept in sync with positioning logic)
const TOOLTIP_MAX_HEIGHT = 600; // px max height before scrolling
const TARGET_GAP = 16; // px spacing from target highlight

// Simple markdown parser for bold text (**text**)
function parseMarkdownBold(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  const regex = /\*\*(.+?)\*\*/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    // Add bold text
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

interface TutorialTooltipProps {
  /** Current step configuration */
  step: TutorialStep;

  /** Current step index (0-based) */
  stepIndex: number;

  /** Total number of steps */
  totalSteps: number;

  /** Called when user clicks "Next" */
  onNext: () => void;

  /** Called when user clicks "Skip Tutorial" */
  onSkip: (neverShowAgain: boolean) => void;

  /** Whether this is the last step */
  isLastStep: boolean;

  /** Optional content to display above the description */
  helperContent?: ReactNode;

  /** Whether actions are currently executing */
  isExecutingAction?: boolean;
}

export function TutorialTooltip({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onSkip,
  isLastStep,
  helperContent,
  isExecutingAction = false,
}: TutorialTooltipProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [neverShowAgain, setNeverShowAgain] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate tooltip position relative to target
  const updatePosition = useCallback(() => {
    const tooltipWidth = TOOLTIP_WIDTH;
    // Get actual rendered height, or use max height as fallback
    const tooltipHeight = tooltipRef.current?.offsetHeight || TOOLTIP_MAX_HEIGHT;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Collect union rect of targets
    const targets = Array.from(document.querySelectorAll<HTMLElement>(step.targetSelector));
    if (targets.length === 0 || step.targetSelector === 'none') {
      // Center in viewport
      const top = Math.max(16, (viewportHeight - tooltipHeight) / 2);
      const left = Math.max(16, (viewportWidth - tooltipWidth) / 2);
      setPosition({ top, left });
      return;
    }

    const rects = targets.map((el) => el.getBoundingClientRect());
    const targetRect = {
      top: Math.min(...rects.map((r) => r.top)),
      left: Math.min(...rects.map((r) => r.left)),
      right: Math.max(...rects.map((r) => r.right)),
      bottom: Math.max(...rects.map((r) => r.bottom)),
    };
    const targetWidth = targetRect.right - targetRect.left;
    const targetHeight = targetRect.bottom - targetRect.top;

    const placements: Array<'top' | 'bottom' | 'left' | 'right'> = (() => {
      if (step.placement === 'auto') return ['bottom', 'top', 'right', 'left'];
      const preferred = step.placement;
      const others = ['top', 'bottom', 'left', 'right'].filter((p) => p !== preferred) as Array<'top' | 'bottom' | 'left' | 'right'>;
      return [preferred, ...others];
    })();

    const padding = 16;

    const intersects = (box: { top: number; left: number; width: number; height: number }) => {
      const boxRight = box.left + box.width;
      const boxBottom = box.top + box.height;
      const expanded = {
        left: targetRect.left - TARGET_GAP,
        right: targetRect.right + TARGET_GAP,
        top: targetRect.top - TARGET_GAP,
        bottom: targetRect.bottom + TARGET_GAP,
      };
      return !(
        boxRight < expanded.left ||
        box.left > expanded.right ||
        boxBottom < expanded.top ||
        box.top > expanded.bottom
      );
    };

    const clamp = (pos: { top: number; left: number }) => {
      let { top, left } = pos;
      if (left < padding) left = padding;
      if (left + tooltipWidth > viewportWidth - padding) left = viewportWidth - tooltipWidth - padding;
      if (top < padding) top = padding;
      if (top + tooltipHeight > viewportHeight - padding) top = viewportHeight - tooltipHeight - padding;
      return { top, left };
    };

    const computePos = (placement: 'top' | 'bottom' | 'left' | 'right') => {
      switch (placement) {
        case 'top':
          return {
            top: targetRect.top - tooltipHeight - TARGET_GAP,
            left: targetRect.left + targetWidth / 2 - tooltipWidth / 2,
          };
        case 'bottom':
          return {
            top: targetRect.bottom + TARGET_GAP,
            left: targetRect.left + targetWidth / 2 - tooltipWidth / 2,
          };
        case 'left':
          return {
            top: targetRect.top + targetHeight / 2 - tooltipHeight / 2,
            left: targetRect.left - tooltipWidth - TARGET_GAP,
          };
        case 'right':
          return {
            top: targetRect.top + targetHeight / 2 - tooltipHeight / 2,
            left: targetRect.right + TARGET_GAP,
          };
      }
    };

    // Try placements in order; pick the first that doesn’t overlap
    let chosen = clamp(computePos(placements[0]));
    for (const p of placements) {
      const candidate = clamp(computePos(p));
      if (!intersects({ top: candidate.top, left: candidate.left, width: tooltipWidth, height: tooltipHeight })) {
        chosen = candidate;
        break;
      }
    }

    setPosition(chosen);
  }, [step.targetSelector, step.placement]);

  useEffect(() => {
    // Initial position update
    updatePosition();

    // Update again after a small delay to account for content rendering
    const timeoutId = setTimeout(updatePosition, 50);

    const handleUpdate = () => {
      requestAnimationFrame(updatePosition);
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [updatePosition]);

  // Auto-focus tooltip when step changes for accessibility
  useEffect(() => {
    tooltipRef.current?.focus();
  }, [stepIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key - skip tutorial
      if (e.key === 'Escape') {
        e.preventDefault();
        if (showSkipConfirm) {
          // If already in confirmation, execute skip
          onSkip(neverShowAgain);
        } else {
          // First press shows confirmation
          setShowSkipConfirm(true);
        }
        return;
      }

      // Right arrow or Enter - next step (if not executing actions)
      if ((e.key === 'ArrowRight' || e.key === 'Enter') && !isExecutingAction) {
        e.preventDefault();
        if (showSkipConfirm) {
          // Cancel skip confirmation
          handleCancelSkip();
        } else {
          // Advance to next step
          onNext();
        }
        return;
      }

      // Cancel skip confirmation with Escape when already in confirm mode
      // (handled above in Escape logic)
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExecutingAction, showSkipConfirm, neverShowAgain, onNext, onSkip]);

  const handleSkipClick = () => {
    if (showSkipConfirm) {
      onSkip(neverShowAgain);
    } else {
      setShowSkipConfirm(true);
    }
  };

  const handleCancelSkip = () => {
    setShowSkipConfirm(false);
    setNeverShowAgain(false);
  };

  return (
    <FocusTrap
      focusTrapOptions={{
        initialFocus: () => tooltipRef.current ?? undefined,
        fallbackFocus: () => tooltipRef.current ?? document.body,
        escapeDeactivates: false, // We handle Escape ourselves
        clickOutsideDeactivates: false, // Prevent clicks outside from deactivating
        allowOutsideClick: (e: MouseEvent | TouchEvent) => {
          // Allow tutorial-initiated clicks (marked with _tutorialClick flag)
          const target = e.target as HTMLElement & { _tutorialClick?: boolean };
          if (target._tutorialClick) {
            return true;
          }
          // Block all other clicks outside the trap
          return false;
        },
      }}
    >
      <div
        ref={tooltipRef}
        data-tutorial-ui
        className="fixed bg-white rounded-2xl shadow-modal p-6"
        style={{
          top: position.top,
          left: position.left,
          width: TOOLTIP_WIDTH,
          maxHeight: TOOLTIP_MAX_HEIGHT,
          zIndex: zIndex.tutorialTooltip,
        }}
        role="dialog"
        aria-labelledby="tutorial-title"
        aria-describedby="tutorial-description"
        aria-modal="true"
        tabIndex={-1}
      >
      <div className="flex min-h-0 flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Pill variant="info" aria-live="polite">
            {`Step ${stepIndex + 1} ${ui.labels.of} ${totalSteps}`}
          </Pill>
        </div>

        {/* Title */}
        <h3 id="tutorial-title" className="text-xl font-bold text-text-theme-primary">{step.title}</h3>

        {/* Progress dots directly under title */}
        <div className="flex gap-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-2 w-2 rounded-full transition-colors ${
                i === stepIndex ? 'bg-primary' : i < stepIndex ? 'bg-brand-300' : 'bg-surface-tertiary'
              }`}
            />
          ))}
        </div>

        {/* Body: scrollable content so footer stays glued to bottom */}
        <div className="flex-1 min-h-0 overflow-auto">
          {/* Description */}
          <div id="tutorial-description" className="text-text-theme-primary">
            <p className="m-0 whitespace-pre-line">
              {step.description.split('\n').map((line, i) => (
                <span key={i}>
                  {i > 0 && <br />}
                  {parseMarkdownBold(line)}
                </span>
              ))}
            </p>
          </div>

          {/* Helper actions (e.g., "Type me an example" button) */}
          {/* TODO: Render helperActions properly - need to execute the actions when buttons clicked */}
          {/* {step.helperActions && <div className="border-t border-slate-200 pt-4">{step.helperActions}</div>} */}

          {/* Additional helper content (deprecated, use step.helperActions instead) */}
          {helperContent && <div className="border-t border-border pt-4">{helperContent}</div>}
        </div>

        {/* Actions */}
        <div className="mt-auto flex items-center justify-between">
          <div className="flex flex-col gap-2 items-start">
            <div className="flex gap-2 items-center">
              <button onClick={handleSkipClick} className={`${buttonVariants.link} shrink-0`} data-testid="tutorial-skip-button">
                {showSkipConfirm ? 'Confirm Skip' : ui.actions.skip}
              </button>
              {showSkipConfirm && (
                <button onClick={handleCancelSkip} className={buttonVariants.secondary}>
                  {ui.actions.cancel}
                </button>
              )}
            </div>

            {/* Skip confirmation checkbox */}
            {showSkipConfirm && (
              <label className="flex items-center gap-2 text-sm text-text-theme-primary pl-4">
                <input
                  type="checkbox"
                  checked={neverShowAgain}
                  onChange={(e) => setNeverShowAgain(e.target.checked)}
                  className="rounded border-border"
                />
                {"Don’t show this tutorial again"}
              </label>
            )}
          </div>

          <div className="flex gap-2">
            {isExecutingAction ? (
              <div className="flex items-center gap-2 text-primary transition-opacity duration-200 opacity-100" role="status" aria-live="polite" data-testid="tutorial-processing">
                <Spinner size="md" />
                <span className="font-medium">{ui.loading.processing}</span>
              </div>
            ) : (
              <button
                onClick={onNext}
                className={`${buttonVariants.primary} transition-opacity duration-200`}
                data-testid="tutorial-next-button"
              >
                {isLastStep ? ui.actions.finish : (step.nextButtonLabel || ui.actions.next)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    </FocusTrap>
  );
}
